import { defineStore } from 'pinia'

import type { Station as ServiceStation } from '@models/api/station/Station'
import { Station } from 'src/models/Station'
import { CoordinateHelper } from '@helpers/CoordinateHelper'
import api from 'src/api'
import { RegexHelper } from '@helpers/RegexHelper'
import { ArcgisGeometryHelper } from '@mapping/arcgis/ArcgisGeometryHelper'
import { contains } from '@arcgis/core/geometry/geometryEngine'
import Point from '@arcgis/core/geometry/Point'
import Polygon from '@arcgis/core/geometry/Polygon'
import { StationsGeojson } from '@helpers/StationsBuffer'
import { computed, ref } from 'vue'
import {
  HourlyStationVariable,
  HourlyStationVariableID
} from '@models/station/HourlyStationVariable'
import {
  HistoricStationVariable,
  HistoricStationVariableID
} from '@models/station/HistoricStationVariable'

let stationsBuffer: Polygon
function loadStationsBuffer() {
  stationsBuffer = ArcgisGeometryHelper.geoJSONPolygonToArcGISPolygon(
    StationsGeojson.features[0]
  )
}

function isPointInsideBuffer(lat: number, lon: number) {
  if (!stationsBuffer) {
    loadStationsBuffer()
  }
  return contains(
    stationsBuffer,
    new Point({
      x: lon,
      y: lat,
      spatialReference: {
        wkid: 4326
      }
    })
  )
}

function sortByName(stations: Station[]) {
  return stations.sort((a, b) => {
    return a.getName().localeCompare(b.getName())
  })
}

export const useStationsStore = defineStore('stations', () => {
  const stations = ref<Station[]>([])
  const hourlyStationVariables: HourlyStationVariable[] = Object.values(
    HourlyStationVariableID
  )
    .filter((x) => x !== HourlyStationVariableID.WIND_DIRECTION)
    .map((variable) => new HourlyStationVariable(variable))
  const historicStationVariables: HistoricStationVariable[] = Object.values(
    HistoricStationVariableID
  ).map((variable) => new HistoricStationVariable(variable))
  const selectedStation = ref<Station>()

  const stationsByID = (ids: number[]) => {
    return sortByName(stations.value.filter((x) => ids.includes(x.id)))
  }

  const stationByID = (id: number) => {
    return stations.value.find((x) => x.id === id)
  }

  const filterByName = (query: string) => {
    let filteredStations = stations.value.filter((x) => {
      const nameCheck = RegexHelper.like(x.getName(), query)

      if (nameCheck && !x.isSynop()) {
        return x
      }
    })
    filteredStations = sortByName(filteredStations)
    return filteredStations
  }

  const stationsSources = computed(() => {
    const allSources = stations.value.map((station) => station.source)
    return [...new Set(allSources)]
  })

  function findClosestTo(
    latitude: number,
    longitude: number,
    type?: string,
    maxDistance = 60
  ) {
    const typeFilteredStations = type
      ? stations.value.filter((x) => x.aemetType === type)
      : stations.value

    let closestStation: Station | null = typeFilteredStations.reduce((a, b) => {
      const distA = CoordinateHelper.distanceBetweenPoints(
        a.latitude,
        a.longitude,
        latitude,
        longitude
      )
      const distB = CoordinateHelper.distanceBetweenPoints(
        b.latitude,
        b.longitude,
        latitude,
        longitude
      )
      return distA < distB ? a : b
    })

    const closestDist = CoordinateHelper.distanceBetweenPoints(
      closestStation.latitude,
      closestStation.longitude,
      latitude,
      longitude
    )

    const isInsideBuffer = isPointInsideBuffer(latitude, longitude)
    if (!closestStation || closestDist >= maxDistance || !isInsideBuffer) {
      closestStation = null
    }

    return closestStation
  }

  function findNClosestTo(
    nStations: number,
    latitude: number,
    longitude: number,
    type?: string,
    maxDistance = 20
  ) {
    const typeFilteredStations = type
      ? stations.value.filter((x) => x.aemetType === type)
      : stations.value

    const distances = typeFilteredStations.map((x) => ({
      station: x,
      distance: CoordinateHelper.distanceBetweenPoints(
        x.latitude,
        x.longitude,
        latitude,
        longitude
      )
    }))

    // Sort stations based on distance
    distances.sort((a, b) => a.distance - b.distance)

    // Return the N closest stations
    const isInsideBuffer = isPointInsideBuffer(latitude, longitude)

    const nClosest = distances
      .slice(0, nStations)
      .filter((entry) => {
        const station = entry.station

        if (station && entry.distance <= maxDistance && isInsideBuffer) {
          return entry.station
        }
      })
      .map((x) => x.station)

    return nClosest
  }

  function SET_STATIONS(serviceStations: ServiceStation[]) {
    const stationsValue: Station[] = []

    serviceStations.forEach((serviceStation) => {
      const station = new Station(serviceStation)
      stationsValue.push(station)
    })

    stations.value = stationsValue
  }

  async function fetchAllStations() {
    try {
      const response = await api.stations.getAllStations()
      SET_STATIONS(response.data)
    } catch (err) {
      console.error(err)
      throw err
    }
  }

  function SET_SELECTED_STATION(station: Station) {
    selectedStation.value = station
  }

  return {
    stations,
    selectedStation,
    stationsByID,
    stationByID,
    filterByName,
    findClosestTo,
    findNClosestTo,
    fetchAllStations,
    SET_STATIONS,
    stationsSources,
    SET_SELECTED_STATION,
    hourlyStationVariables,
    historicStationVariables
  }
})
